import { CMSChildrenContainer } from '@/cmsComponents/ChildrenContainer';
import cmsStyles from '@/cmsComponents/style.module.scss';
import cx from 'classnames';
import { PATH } from 'constants/path';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import type { Control } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { FormEmail, FormModule, Forms } from 'types/cms';
import { saleforceDateFormat } from 'utils/dateHelper';
import { postLeadforms } from 'utils/useRequest';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';

import TextInput from './TextInput';
import styles from './style.module.scss';

const MessageBox = dynamic(() => import('@/components/MessageBox').then(mod => mod.MessageBox));
const Checkbox = dynamic(() => import('@/cmsComponents/Form/Checkbox').then(mod => mod.Checkbox));
const CheckboxGroup = dynamic(() =>
  import('@/cmsComponents/Form/Checkbox').then(mod => mod.CheckboxGroup),
);
const DateInput = dynamic(() => import('@/cmsComponents/Form/DateInput'));
const Disclaimer = dynamic(() => import('@/cmsComponents/Form/Disclaimer'));
const Dropdown = dynamic(() => import('@/cmsComponents/Form/Dropdown'));
const RadioInput = dynamic(() => import('@/cmsComponents/Form/RadioInput'));
const TextAreaLong = dynamic(() => import('@/cmsComponents/Form/TextAreaLong'));

const EmailInput = (props: FormEmail & { control: Control }) => (
  <TextInput {...props} type="email" />
);

const hiddenComponent = 'forms-collection.hidden';

const FormFieldMap = {
  text: TextInput,
  email: EmailInput,
  checkbox: Checkbox,
  date: DateInput,
  'picklist-radio': RadioInput,
  'picklist-dropdown': Dropdown,
  'picklist-multi-checkbox': CheckboxGroup,
  disclaimer: Disclaimer,
  'text-area-long': TextAreaLong,
};

export const CMSForm: React.FC<FormModule> = ({
  forms,
  return_url,
  anchor_link_id,
  title,
  alignment,
}) => {
  const { control, handleSubmit, reset } = useForm();
  const router = useRouter();
  const [messageBoxData, setMessageBoxData] = useState({
    showMessageBox: false,
    text: '',
    showButton: true,
  });
  const [isDisabledFormLoading, setIsDisabledFormLoading] = useState(false);

  const onSubmit = data => {
    setIsDisabledFormLoading(true);
    const payloadData = JSON.parse(JSON.stringify(data));
    modifyData(payloadData, forms);
    postLeadforms(payloadData).then(result => {
      if ('response' in result) {
        reset(); // Clear out values so that they are not retained when browser 'back' is used in Chrome
        router.push(return_url);
      } else {
        handleErrors(result.error, setMessageBoxData);
      }
      setIsDisabledFormLoading(false);
    });
  };

  return (
    <CMSChildrenContainer className={styles.formContainer}>
      {title && (
        <Typography
          component="h3"
          variant="h5"
          className={cx(styles.title, alignment === 'Left' ? styles.alignLeft : styles.alignCenter)}
        >
          {title}
        </Typography>
      )}
      {messageBoxData.showMessageBox && (
        <MessageBox
          messageText={messageBoxData.text}
          showButton={messageBoxData.showButton}
          buttonLink={PATH.CONTACT_US}
          buttonText={`Contact Us`}
        ></MessageBox>
      )}

      <form
        id={anchor_link_id || undefined}
        className={cx(styles.form, cmsStyles.containerPaddingS, styles[alignment])}
        onSubmit={handleSubmit(onSubmit)}
      >
        {forms.map(form => {
          return form.form_fields.map((field, index) => {
            const fieldType = field.__component.split('.')[1];
            const Comp = FormFieldMap[fieldType];
            if (!Comp) {
              if (field.__component !== hiddenComponent) {
                console.warn(`Unable to render component '${fieldType}'.`);
              }
              return null;
            }
            if ('id' in field) {
              const key = `${field.id}_${field.__component}`;
              return <Comp key={key} {...field} control={control} />;
            }
            return <Comp key={index} {...field} control={control} />;
          });
        })}
        <Button
          type="submit"
          variant="contained"
          fullWidth={true}
          color="secondary"
          disabled={isDisabledFormLoading}
          className={styles[alignment]}
        >
          {isDisabledFormLoading ? (
            <CircularProgress size={30} className={styles.spinner} />
          ) : (
            'Submit'
          )}
        </Button>
      </form>
    </CMSChildrenContainer>
  );
};

/**
 * Adds hidden fields, formats dates and turns multi-checklist into string
 * FIXME this should only add values for hidden fields, and any other data maniplation should be done server side
 * @param payloadData - current input values
 * @param forms
 */
const modifyData = (payloadData: { [key: string]: any }, forms: Forms[]) => {
  forms.forEach(form => {
    form.form_fields.forEach(field => {
      if (field.__component === hiddenComponent) {
        payloadData[field.identifier] = field.value;
      } else if (field.__component === 'forms-collection.date') {
        payloadData[field.identifier] = saleforceDateFormat(payloadData[field.identifier]);
      } else if (field.__component === 'forms-collection.picklist-multi-checkbox') {
        payloadData[field.identifier] = Object.keys(payloadData[field.identifier])
          .reduce<string[]>((acc, curr) => {
            if (payloadData[field.identifier][curr]) {
              acc.push(curr);
            }
            return acc;
          }, [])
          .join(';');
      }
    });
  });
};

const handleErrors = (
  error,
  setMessageBoxData: React.Dispatch<
    React.SetStateAction<{ showMessageBox: boolean; text: string; showButton: boolean }>
  >,
) => {
  let messageBoxText = `We're having trouble submitting your form. Please try again, and if that fails please contact us for assistance.`;
  let showButton = true;

  if (!!error.statusCode && error.statusCode === 400 && error.message) {
    showButton = false;

    if (typeof error.message === 'string') {
      messageBoxText = `We're having trouble submitting your form. This is the issue with the form: ${error.message}`;
    }
    if (Array.isArray(error.message) && error.message.length === 1) {
      messageBoxText = `We're having trouble submitting your form. This is the issue with the form: ${error.message[0]}`;
    }
    if (Array.isArray(error.message) && error.message.length > 1) {
      messageBoxText = `We're having trouble submitting your form. These are the issues with the form: `;
      error.message.forEach(msg => {
        messageBoxText = `${messageBoxText} ${msg},`;
      });
      messageBoxText = messageBoxText.slice(0, -1);
    }
  }

  setMessageBoxData({ showMessageBox: true, text: messageBoxText, showButton: showButton });
  console.error('Unable to submit the form because of the following error: ', error);
};
